import { Link, useParams, useSearchParams, useNavigate, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from "../../components/ConfirmationModal"
import DebugAccordion from "../../components/DebugAccordion"
import JsonForm from "../../components/JsonForm.jsx"
import PageTitle from "../../components/PageTitle.jsx"
import UtgNav from "./components/UtgNav"
import UtgTools from "./components/UtgTools.jsx";
import { addNotification, NotificationTypes } from "../../stores/notification-store.js";
import { checkApiInterfaceConflicts, isEmptyObject, makeFieldsReadonly } from '../../utils/utils.js'
import { UpdateComponentConfig } from "../../utils/api"

import useComponentConfigData, { componentConfigData } from "../../hooks/useComponentConfigData.js"
import useComponentConfigSchema, { componentConfigSchema } from "../../hooks/useComponentConfigSchema.js"
import useUtgData from "../../hooks/useUtgData"

export default function ApiInterfaceDetail() {

    const navigate = useNavigate();
    const { beId, componentId, id } = useParams()
    const [searchParams] = useSearchParams()
    const port = searchParams.get('port')
    const editMode = !!port
    const debug = !!searchParams.get('debug')
    const {config, accessToken, canEditSignal, accordionData: sidebarAccordionData  } = useOutletContext()

    const { utg, loading: utgLoading, error: utgError } = useUtgData(beId, componentId, config, accessToken)
    const { loading: componentConfigSchemaLoading } = useComponentConfigSchema(beId, utg.value.UtgVersion, config, accessToken)
    const apiInterfaceSchema = useComputed(() => {
        if (componentConfigSchema.value.config) {
            // extract the selected api interface schema from the component config schema
            const jsonFormConfigSchema = { config: componentConfigSchema.value?.config?.properties?.apiInterfaceConfig?.properties?.apiInterface?.items?.find(a => a.properties.cdType["s4-model-key"] == parseInt(id, 10)) }
            return jsonFormConfigSchema
        }
        return {}
    })
    const { loading: componentConfigLoading, modified } = useComponentConfigData(beId, utg.value, config, accessToken)
    const apiInterface = useComputed(() => {
        if (componentConfigData.value.componentConfig && editMode) {
            const index = componentConfigData.value.componentConfig.config.apiInterfaceConfig.apiInterface.findIndex(a => a.listenPort == port)
            const currentApiInterface = componentConfigData.value.componentConfig.config.apiInterfaceConfig.apiInterface[index]
            const jsonFormApiInterface = { config: currentApiInterface }
            return jsonFormApiInterface
        }
        return null
    })
    const apiInterfaceIndex = useComputed(() => {
        if (componentConfigData.value.componentConfig && editMode) {
            // selected apiInterface index. This is used when editing an interface since there are no unique id's
            const index = componentConfigData.value.componentConfig.config.apiInterfaceConfig.apiInterface.findIndex(a => a.listenPort == port)
            return index
        }
        return -1
    })
    const pageTitle = useComputed(() => {
        const currentCaption = apiInterfaceSchema.value?.config?.properties?.cdType['s4-caption'] || ''
        return editMode ?
        `Manage ${currentCaption} UTG API Interface` :
        `Add ${currentCaption} UTG API Interface`
    })

    const jsonFormData = useComputed(() => {

        if (isEmptyObject(componentConfigSchema.value) || isEmptyObject(componentConfigData.value)) {
            return
        }
        
        // deep clone the schema
        let clonedSchema = structuredClone(apiInterfaceSchema.value)

        // disable editing of all fields if canEditSignal is false
        if (!canEditSignal.value) {
            clonedSchema = makeFieldsReadonly(clonedSchema)
        }

        // sets the variable on the global windows object so that shift-utils.js code can determine if we are working with a profile or config schema
        globalThis.SCHEMA_MODE = 'CONFIG'
        console.log('Set SCHEMA_MODE to', globalThis.SCHEMA_MODE)

        const formData = {
            schema: clonedSchema,
            loadedComponentConfig: componentConfigData.value.componentConfig.config,
            onSubmitValid: handleFormSubmit,
            onCancel: handleCancel,
            onFormChange: handleFormChange,
            canEditConfig: canEditSignal.value
        }
        if (apiInterface.value)
        {
            formData.value = apiInterface.value
        }
        console.log('formData from ApiInterfaceDetail', formData)
        return formData
    }) 

    const showCancelModal = useSignal(false)
    const isFormEdited = useSignal(false)
    const isFormSubmitting = useSignal(false)
    const accordionData = [
        { name: 'API Interface Data', data: apiInterface },
        { name: 'API Interface Schema', data: apiInterfaceSchema },
        { name: 'API Interface Index', data: apiInterfaceIndex },
        { name: 'Component Config', data: componentConfigData },
        { name: 'Component Schema', data: componentConfigSchema },
        { name: 'UTG Data', data: utg },
        { name: 'Access Token', data: accessToken},
        { name: 'App Config', data: config}
    ]

    const handleFormSubmit = values => {
        // short-circuit if the handler was already called. This blocks clicking the submit a second time while already processing a previous submit
        if (isFormSubmitting.value) {
            return false
        }
        isFormSubmitting.value = true
        try {
            console.log('values', values)
            if (values) {
                
                // build the full config data including apiInterface and deviceConfig data to save back to server
                let data = {
                    config: componentConfigData.value.componentConfig.config,
                    hash: componentConfigData.value.componentConfig.hash,
                    isCommitted: componentConfigData.value.componentConfig.isCommitted,
                    lastUpdatedAt: componentConfigData.value.componentConfig.lastUpdatedAt,
                    lastUpdatedBy: componentConfigData.value.componentConfig.lastUpdatedBy,
                    revisionId: componentConfigData.value.componentConfig.revisionId,
                    componentId: componentConfigData.value.componentConfig.componentId
                }

                // make sure the apiInterfaceConfig is present before adding to it
                data.config.apiInterfaceConfig = data.config.apiInterfaceConfig || {}
                data.config.apiInterfaceConfig.apiInterface = data.config.apiInterfaceConfig.apiInterface || []

                if (editMode) {
                    // replace the existing selected apiInterface with data from values 
                    data.config.apiInterfaceConfig.apiInterface[apiInterfaceIndex.value] = values.config
                }
                else {
                    // insert values into the apiInterface array
                    data.config.apiInterfaceConfig.apiInterface.push(values.config)
                }

                // validate listenPort uniqueness in case jsonform validation fails
                if (checkApiInterfaceConflicts(data.config.apiInterfaceConfig.apiInterface)) {
                    addNotification("Unable to save due to conflict. All API Interfaces must have a unique Port.", NotificationTypes.ERROR)
                    return false
                }

                console.log(data)
                UpdateComponentConfig(beId, utg.value.MachinePermanentId, utg.value.ComponentPermanentId, data, config, accessToken)
                    .then(result => {
                        // componentConfigData.value = result
                        modified.value = true
                        addNotification('Api Interface was successfully updated.', NotificationTypes.SUCCESS, true)
                        navigate('../')
                    })
                    .catch(error => {
                        console.log(error.toString())
                        addNotification("Unable to update the Api Interface.")
                        return false
                    })
                    .finally(() => {
                        isFormSubmitting.value = false
                        // must return false to avoid postback
                        return false
                    })
            }
        }
        catch (error) {
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    const handleCancel = () => {
        if (isFormEdited.value) {
            showCancelModal.value = true
        }
        else {
            navigate('../apiinterfaces')
        }
    }

    const handleFormChange = () => {
        isFormEdited.value = true
    }

    if (utgLoading.value || componentConfigLoading.value || componentConfigSchemaLoading.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
        <ConfirmationModal show={showCancelModal.value} handleCancel={() => showCancelModal.value = false} handleConfirm={() => navigate('../apiinterfaces')} message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'} title={"Warning: Unsaved Changes"} cancelText="Continue Editing" confirmText="Lose Changes" />
        <Breadcrumb className="px-3 pt-1 bg-light">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/utgs` }}>
                UTG List
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Manage {utg.value.ComponentNickname}</Breadcrumb.Item>
        </Breadcrumb>
        <Stack direction="horizontal" gap={3}>
            <PageTitle title={pageTitle.value} canEditSignal={canEditSignal} />
            <UtgTools componentConfigData={componentConfigData.value}></UtgTools>
        </Stack>
        <UtgNav beId={beId} componentId={componentId} activeKey="2"></UtgNav>
        <JsonForm data={jsonFormData.value}></JsonForm>
        <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}